import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { CreateProjectRequest } from 'generated';
import { useNavParams } from 'hooks';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';

import { ControlledDatePicker, ControlledTextField } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';

import { addEditProjectSchema } from '../EditProjectPage/add-edit-project-schema';

type Form = InferType<typeof addEditProjectSchema>;

const defaultValues = {
  name: '',
  description: '',
  validFrom: dayjs().startOf('day'),
  validTo: dayjs().add(1, 'day')
};
type AddProjectFormProps = {
  handleClose: () => void;
};

export default function AddProjectForm({ handleClose }: AddProjectFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: createProject, isLoading } = mutations.useCreateProject();
  const { setParams } = useNavParams();

  const methods = useForm<Form>({
    defaultValues,
    resolver: yupResolver(addEditProjectSchema)
  });

  async function onSubmit(data: Form) {
    const { name, description, validFrom, validTo } = data;
    const basePayload = {
      name,
      description,
      validFrom: validFrom.format('YYYY-MM-DD'),
      validTo: validTo.format('YYYY-MM-DD')
    };

    const payload: CreateProjectRequest = basePayload;

    createProject(payload, {
      onSuccess: (res) => {
        queryClient.invalidateQueries(queryKeys.getProjects.name);
        showSuccessSnackbar();
        setParams({ projectId: res });
      },
      onError: () => {
        showErrorSnackbar();
      }
    });
  }

  return (
    <MultiStepForm methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <MultiStepForm.Section title="Wat" validateFields={['name']}>
        <ControlledTextField
          name="name"
          control={methods.control}
          label={t('name')}
          variant="outlined"
        />
        <ControlledTextField
          name="description"
          control={methods.control}
          label={t('description')}
          variant="outlined"
        />
      </MultiStepForm.Section>
      <MultiStepForm.Section
        title={t('validity')}
        validateFields={['validFrom', 'validTo']}
      >
        <Box display="flex" gap={1}>
          <ControlledDatePicker
            name="validFrom"
            control={methods.control}
            label={t('validFrom')}
            variant="outlined"
          />
          <ControlledDatePicker
            name="validTo"
            control={methods.control}
            label={t('validTo')}
            variant="outlined"
          />
        </Box>
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <Button
          type="button"
          variant="text"
          color="primary"
          onClick={() => {
            handleClose();
          }}
        >
          {t('cancel')}
        </Button>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
}
